import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Minus, TrendingDown, TrendingUp } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, PillTabs, RelativeTime, StatusBadge, formatDate, formatNumber } from '../../../components/design-system/admin';
import type { FeatureReviewResponse, FeatureReviewRow, FeatureWindow, Trend, Verdict } from '../../../common/models/unit-economics.model';
import { money } from '../costs/cost-format';
import { evidenceText, verdictTone } from './ue-format';

type VerdictFilter = Verdict | 'all';
const ORDER: Record<Verdict, number> = { review: 0, watch: 1, keep: 2 };

function Windows({ w, strong }: { w: FeatureWindow; strong?: boolean }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 tabular-nums">
      <span className="text-muted-foreground">{w.d7}</span>
      <span className="text-muted-foreground">{w.d30}</span>
      <span className={cn(strong && 'font-medium')}>{w.d60}</span>
    </span>
  );
}

function TrendMark({ trend }: { trend: Trend }) {
  const { t } = useTranslation();
  const Icon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
      <Icon className="size-3.5" />
      {t(`UNIT_ECONOMICS.TREND.${trend}`)}
    </span>
  );
}

/**
 * Every feature, judged by who really uses it: paying customers and all real
 * accounts over 7, 30 and 60 days, the trend, what it costs us, and the
 * verdict with its evidence. Review comes first. A row opens who used it.
 */
export function FeatureReviewPanel({ data }: { data: FeatureReviewResponse | null }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<VerdictFilter>('all');
  const [open, setOpen] = useState<Set<string>>(new Set());

  const rows = useMemo(
    () =>
      (data?.features || [])
        .filter((f) => filter === 'all' || f.verdict === filter)
        .sort((a, b) => ORDER[a.verdict] - ORDER[b.verdict] || b.paying.d60 - a.paying.d60 || a.key.localeCompare(b.key)),
    [data, filter],
  );
  const toggle = (k: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const why = (f: FeatureReviewRow) =>
    f.evidence.map((e) => {
      const { key, values } = evidenceText(e, (d) => formatDate(d), (u) => money(u));
      return t(key, values);
    });
  const days = data?.windowDays ?? 60;

  return (
    <div className="flex flex-col gap-3" data-testid="ue-review">
      <div className="flex flex-col gap-1">
        <p className="max-w-3xl text-sm text-muted-foreground">{t('UNIT_ECONOMICS.REVIEW.DESCRIPTION', { days })}</p>
        {data && (
          <p className="max-w-3xl text-xs text-muted-foreground" data-testid="ue-review-rule">
            {t('UNIT_ECONOMICS.REVIEW.RULE', { n: data.assumptions.review.maxPaying, days, newDays: data.assumptions.review.newFeatureDays })}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <PillTabs<VerdictFilter>
          value={filter}
          onChange={setFilter}
          ariaLabel={t('UNIT_ECONOMICS.REVIEW.TITLE')}
          options={[
            { value: 'all', label: t('UNIT_ECONOMICS.REVIEW.FILTER_ALL'), count: data?.features.length },
            { value: 'review', label: t('UNIT_ECONOMICS.REVIEW.VERDICT.review'), count: data?.counts.review },
            { value: 'watch', label: t('UNIT_ECONOMICS.REVIEW.VERDICT.watch'), count: data?.counts.watch },
            { value: 'keep', label: t('UNIT_ECONOMICS.REVIEW.VERDICT.keep'), count: data?.counts.keep },
          ]}
        />
        {data && <span className="text-xs text-muted-foreground">{t('UNIT_ECONOMICS.REVIEW.SUMMARY', { paying: data.payingCustomers, accounts: data.accounts })}</span>}
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        {!data ? (
          <div className="grid gap-2 p-4">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-11 w-full" />
            ))}
          </div>
        ) : !rows.length ? (
          <EmptyState title={t('UNIT_ECONOMICS.REVIEW.EMPTY')} />
        ) : (
          <Table data-testid="ue-review-table">
            <TableHeader>
              <TableRow>
                <TableHead className="ps-4">{t('UNIT_ECONOMICS.REVIEW.FEATURE')}</TableHead>
                <TableHead>{t('UNIT_ECONOMICS.REVIEW.EVIDENCE')}</TableHead>
                <TableHead className="text-end">
                  <div>{t('UNIT_ECONOMICS.REVIEW.PAYING')}</div>
                  <div className="text-[11px] font-normal text-muted-foreground">{t('UNIT_ECONOMICS.REVIEW.WINDOWS')}</div>
                </TableHead>
                <TableHead className="text-end">
                  <div>{t('UNIT_ECONOMICS.REVIEW.ALL')}</div>
                  <div className="text-[11px] font-normal text-muted-foreground">{t('UNIT_ECONOMICS.REVIEW.WINDOWS')}</div>
                </TableHead>
                <TableHead>{t('UNIT_ECONOMICS.REVIEW.TREND')}</TableHead>
                <TableHead>{t('UNIT_ECONOMICS.REVIEW.LAST_USE')}</TableHead>
                <TableHead className="pe-4 text-end">{t('UNIT_ECONOMICS.REVIEW.COST')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((f) => {
                const expanded = open.has(f.key);
                return (
                  <Fragment key={f.key}>
                    <TableRow className="cursor-pointer align-top" onClick={() => toggle(f.key)} aria-expanded={expanded} data-testid={`ue-review-row-${f.key}`}>
                      <TableCell className="ps-4">
                        <div className="flex items-center gap-1.5">
                          {expanded ? <ChevronDown className="size-3.5 text-muted-foreground" /> : <ChevronRight className="size-3.5 text-muted-foreground rtl:rotate-180" />}
                          <span className="font-medium">{t(`UNIT_ECONOMICS.FEATURES.${f.key}`)}</span>
                          {f.launchedAt && (
                            <Badge variant="outline" className="font-normal">
                              {t('UNIT_ECONOMICS.REVIEW.NEW')}
                            </Badge>
                          )}
                        </div>
                        <div className="mt-1 flex items-center gap-2 ps-5">
                          <StatusBadge tone={verdictTone(f.verdict)}>{t(`UNIT_ECONOMICS.REVIEW.VERDICT.${f.verdict}`)}</StatusBadge>
                          <span className="text-xs text-muted-foreground">{t(`UNIT_ECONOMICS.GROUPS.${f.group}`)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[340px] text-xs text-muted-foreground">
                        <ul className="flex flex-col gap-0.5">
                          {why(f).map((line, i) => (
                            <li key={i} className={cn(i === 0 && 'text-foreground')}>
                              {line}
                            </li>
                          ))}
                          {f.partial && <li>{t('UNIT_ECONOMICS.REVIEW.PARTIAL')}</li>}
                        </ul>
                      </TableCell>
                      <TableCell className="text-end">
                        <Windows w={f.paying} strong />
                      </TableCell>
                      <TableCell className="text-end">
                        <Windows w={f.all} />
                      </TableCell>
                      <TableCell>
                        <TrendMark trend={f.trend} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {f.lastUseAt ? <RelativeTime value={f.lastUseAt} className="text-muted-foreground" /> : <span className="text-muted-foreground">{t('UNIT_ECONOMICS.TABLE.NEVER')}</span>}
                      </TableCell>
                      <TableCell className="pe-4 text-end tabular-nums">{f.cost60.totalUsd > 0 ? money(f.cost60.totalUsd) : '—'}</TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow className="hover:bg-transparent" data-testid={`ue-review-detail-${f.key}`}>
                        <TableCell colSpan={7} className="bg-muted/30 px-4 py-3">
                          <div className="flex flex-col gap-2 ps-5 text-xs">
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                              {f.firstUseAt && <span>{t('UNIT_ECONOMICS.REVIEW.FIRST_USE', { date: formatDate(f.firstUseAt) })}</span>}
                              <span>{t('UNIT_ECONOMICS.REVIEW.OPENED', { views: formatNumber(f.opened60.views), vendors: f.opened60.vendors })}</span>
                            </div>
                            <div className="font-medium">{t('UNIT_ECONOMICS.REVIEW.USERS')}</div>
                            {f.users.length ? (
                              <ul className="grid gap-1 sm:grid-cols-2">
                                {f.users.map((u) => (
                                  <li key={u.vendorId} className="flex items-baseline justify-between gap-3">
                                    <span className="truncate">
                                      {u.name} <span className="text-muted-foreground">· {t(`UNIT_ECONOMICS.STATUS.${u.status}`)}</span>
                                    </span>
                                    <span className="shrink-0 text-muted-foreground tabular-nums">
                                      {t('UNIT_ECONOMICS.DRAWER.USES', { count: u.uses60 })}
                                      {u.lastUseAt ? ` · ${formatDate(u.lastUseAt)}` : ''}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-muted-foreground">{t('UNIT_ECONOMICS.REVIEW.NO_USERS', { days })}</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
      {data && (
        <p className="text-[11px] text-muted-foreground">{t('UNIT_ECONOMICS.REVIEW.UNMAPPED', { other: money(data.aiCost60.otherUsd), platform: money(data.aiCost60.platformUsd) })}</p>
      )}
    </div>
  );
}
